import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { formatMarkdownLinkAsText } from '../format-markdown-link-as-text';
import { messages } from '../messages';
import type { TeamworkTeam } from '../types';

const pluginInvocationStyles = cssMap({
	teamworkCard: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
		marginBottom: token('space.100'),
	},
	teamworkMembersList: {
		marginTop: token('space.100'),
		marginLeft: token('space.300'),
		maxHeight: '200px',
		overflow: 'auto',
	},
	teamworkItemNumber: {
		minWidth: '24px',
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},
});

export const TeamworkTeamCard = ({
	team,
	index,
}: {
	team: TeamworkTeam;
	index: number;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [showMembers, setShowMembers] = useState(false);

	return (
		<Box xcss={pluginInvocationStyles.teamworkCard}>
			<Stack space="space.100">
				<Inline space="space.100" alignBlock="center">
					<Box xcss={pluginInvocationStyles.teamworkItemNumber}>
						<Text weight="bold" color="color.text">
							{index + 1}.
						</Text>
					</Box>
					<Text weight="bold" color="color.text">
						{team.name || formatMessage(messages.ai_debugger_modal_teamwork_unnamed_team)}
					</Text>
				</Inline>

				{team.description && (
					<Text size="small" color="color.text.subtlest">
						{team.description}
					</Text>
				)}

				<Inline space="space.200" alignBlock="center">
					{team.memberCount !== undefined && (
						<Text size="small">
							{formatMessage(messages.ai_debugger_modal_teamwork_members_count, {
								count: team.memberCount,
							})}
						</Text>
					)}
					{team.teamState && (
						<Text size="small">
							{formatMessage(messages.ai_debugger_modal_teamwork_state, {
								state: team.teamState,
							})}
						</Text>
					)}
				</Inline>

				{team.creator && (
					<Text size="small">
						{formatMessage(messages.ai_debugger_modal_teamwork_creator, {
							creator: team.creator,
						})}
					</Text>
				)}

				{team.ari && (
					<Text size="small" color="color.text.subtlest">
						{team.ari}
					</Text>
				)}

				{team.members.length > 0 && (
					<Box>
						<Button
							iconBefore={showMembers ? ChevronDownIcon : ChevronRightIcon}
							appearance="subtle"
							spacing="compact"
							onClick={() => setShowMembers(!showMembers)}
						>
							{showMembers
								? formatMessage(messages.ai_debugger_modal_teamwork_hide_count, {
										count: team.members.length,
									})
								: formatMessage(messages.ai_debugger_modal_teamwork_show_count, {
										count: team.members.length,
									})}
						</Button>
						{showMembers && (
							<Box xcss={pluginInvocationStyles.teamworkMembersList}>
								<Stack space="space.050">
									{team.members.map((member, idx) => (
										<Text key={idx}>{formatMarkdownLinkAsText(member)}</Text>
									))}
								</Stack>
							</Box>
						)}
					</Box>
				)}
			</Stack>
		</Box>
	);
};
